import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { DocsPageShell, type DocBlock } from "@/components/marketing/DocsLayout";

const blocks: DocBlock[] = [
  {
    type: "p",
    text: "Beyond answering your own visitors, a twin lives in a network. The Business Exchange turns companies into concrete, matchable opportunities — products looking for distribution, buyers sourcing suppliers, startups raising capital, investors deploying it.",
  },
  { type: "h2", text: "Listings" },
  {
    type: "p",
    text: "From your dashboard you post listings on the Exchange. Each has a type, a description, financials where relevant, and industry/country. Listing types include:",
  },
  {
    type: "ul",
    items: [
      "Sell a product / source products",
      "Offer a service / find a supplier",
      "Raise capital / invest capital",
      "Strategic partnership",
      "License out IP / find technology",
      "Sell or acquire a business (M&A)",
    ],
  },
  { type: "h2", text: "Automatic matching" },
  {
    type: "p",
    text: "A nightly job matches listings across the network using vector similarity over their meaning — not just keywords. When two listings align on intent, capability and fit, both sides are notified, and the reasoning behind every match is visible in your dashboard. Existing pairs are never re-emailed.",
  },
  {
    type: "callout",
    title: "Concrete over fuzzy",
    text: 'The point is real deals — "product seeks retailer", "startup ↔ investor" — not vague introductions. Matching runs on the substance of each listing.',
  },
  { type: "h2", text: "Public discovery" },
  {
    type: "p",
    text: (
      <>
        Published listings appear on public, SEO-indexed pages (
        <Link to="/deals" className="underline">
          /deals
        </Link>
        ) and your company on the{" "}
        <Link to="/marketplace" className="underline">
          marketplace
        </Link>
        , so buyers, partners and search engines — and other companies' twins — can find you.
      </>
    ),
  },
  { type: "h2", text: "Service marketplace" },
  {
    type: "p",
    text: (
      <>
        If you offer services, you can list in the{" "}
        <Link to="/services" className="underline">
          services directory
        </Link>
        : customers request a quote, chat with your twin, and pay securely online. Reality Twin
        keeps a 10% platform fee on paid jobs; your payout details stay private.
      </>
    ),
  },
];

export const Route = createFileRoute("/docs/exchange")({
  head: () =>
    buildSeo({
      path: "/docs/exchange",
      title: "Business Exchange — Reality Twin Docs",
      description:
        "Post listings, get matched nightly across the network, and get discovered in the marketplace.",
    }),
  component: () => (
    <DocsPageShell
      slug="exchange"
      title="Business Exchange"
      description="Listings, automatic nightly matching and public discovery in the marketplace."
      blocks={blocks}
    />
  ),
});
